import {
    Deferred,
    Instance,
    Knot,
    Objekt,
    Promize,
    Table,
} from '@siposdani87/sui-js';
import { app } from '../app';
import { AssetService } from '@services/assetService';
import { BudgetService } from '@services/budgetService';
import { CategoryService } from '@services/categoryService';
import { ItemService } from '@services/itemService';
import { LanguageService } from '@services/languageService';
import { UtilService } from '@services/utilService';
import { initTable } from '@utils/collectionSetup';
import { DashboardController } from './dashboardController';

export class BudgetsShowController extends DashboardController {
    static readonly inject = [
        'instances',
        'budgetService',
        'categoryService',
        'itemService',
        'utilService',
        'assetService',
        'languageService',
    ] as const;

    private budgetId!: string;
    private budget!: Objekt;
    private btnBudgetsEditKnot!: Knot<HTMLElement>;
    private btnItemsNewKnot!: Knot<HTMLElement>;
    private btnCategoriesIndexKnot!: Knot<HTMLElement>;
    private organizationId!: string;
    private categoriesTable!: Table;

    constructor(
        instances: Instance,
        private budgetService: BudgetService,
        private categoryService: CategoryService,
        private itemService: ItemService,
        private utilService: UtilService,
        private assetService: AssetService,
        private languageService: LanguageService,
    ) {
        super(instances);
    }

    protected override _initLayout(): void {
        this.budgetId = this.state.getParam('budgetId');
        this.budget = new Objekt();

        this._initToolbarButtons();
        this._initTabPanel('details');
        this._changeToolbarButtons();

        this._initLinks('#details a');
        this._correctDates();

        this._loadBudget().then(() => {
            this._drawContent();
        });
    }

    private _initToolbarButtons(): void {
        this.helper.iconButton('.btn-budgets-index', this.dom, () => {
            this.state.goBack('budgets.index');
        });

        this.btnBudgetsEditKnot = this.helper.fabButton(
            '.btn-budgets-edit',
            this.dom,
            () => {
                this.budgetService
                    .editDialog(this.budget)
                    .then(this.utilService.handleEditResponse('budgets.index'));
            },
        );

        this.btnItemsNewKnot = this.helper.fabButton(
            '.btn-items-new',
            this.dom,
            () => {
                this.itemService.newDialogByBudget(this.budgetId).then(() => {
                    this.state.refresh(true);
                });
            },
        );

        this.btnCategoriesIndexKnot = this.helper.fabButton(
            '.btn-categories-index',
            this.dom,
            () => {
                this.state.go('categories.index', {
                    organizationId: this.organizationId,
                });
            },
        );
    }

    protected override _drawContent(): Promize | boolean {
        this._changeToolbarButtons();

        this._initCategoryTable();
        return true;
    }

    private _changeToolbarButtons(): void {
        this.utilService.showButton(
            this.btnBudgetsEditKnot,
            this.activeTab === 'details',
        );

        this.utilService.showButton(
            this.btnItemsNewKnot,
            this.activeTab === 'category-table',
        );

        this.utilService.showButton(
            this.btnCategoriesIndexKnot,
            this.activeTab === 'category-table',
        );
    }

    private _loadBudget(): Promize {
        const deferred = new Deferred();
        this.budgetService
            .get(this.budgetId, {
                fields: 'id,name,organization_id',
            })
            .then(
                (response) => {
                    this.budget = response.get<Objekt>('budget');
                    this.organizationId = this.budget.get<string>('organization_id');
                    deferred.resolve();
                },
                () => {
                    deferred.reject();
                },
            );
        return deferred.promise();
    }

    private _correctDates(): void {
        this.utilService.correctDate('.start-at', 'format.date', this.dom);
        this.utilService.correctDate('.end-at', 'format.date', this.dom);
    }

    private _initCategoryTable(): void {
        this.categoriesTable = initTable(
            this.categoriesTable,
            this.dom,
            '.categories-table',
            null,
            {
                entityKey: 'categories',
                fetchData: (params) =>
                    this.categoryService.getAllByBudget(
                        this.budgetId,
                        params,
                    ),
                extraParams: (params) => {
                    params.set(
                        'fields',
                        'id,name,type_text,items_count,sum_amount,currency',
                    );
                },
            },
            undefined,
            'category-table',
            this.activeTab,
            {
                no_content: {
                    image_url: this.assetService.getPath(
                        'images/others/no-content.png',
                    ),
                    text: this.languageService.translate('text.no_content'),
                },
                sort: {
                    column: 'name',
                    order: 'asc',
                },
                row_count: 12,
                columns: [
                    'name',
                    'type_text',
                    'items_count',
                    'sum_amount',
                    'actions',
                ],
                sorted: [
                    'name:asc',
                    'items_count:desc',
                    'sum_amount:desc',
                ],
                calculations: {
                    name: (category: Objekt) => {
                        return this.helper.createLink(
                            category.get<string>('name'),
                            (href) => {
                                this.state.go(href);
                            },
                            this.state.resolveUrl('items.index', {
                                budgetId: this.budgetId,
                                categoryId: category.get<string>('id'),
                            }),
                        );
                    },
                    sum_amount: (category: Objekt) => {
                        const sumAmount =
                            category.get<number>('sum_amount');
                        const currency =
                            category.get<string>('currency');
                        return this.languageService.currency(
                            sumAmount,
                            currency,
                        );
                    },
                },
            },
        ) as Table;
    }
}

export const budgetsShowController = app.controller(
    'budgetsShowController',
    BudgetsShowController,
);
